/**
 * Ventana flotante para mostrar cámaras cuando se opera un dispositivo
 * Pantalla negra completa con video arriba, PIN dialog (si aplica) en el medio, y tarjeta del dispositivo abajo
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { VideoStreamViewer } from '../installed/cameras';
import DeviceCard from './DeviceCard';
import PinInputDialog from './PinInputDialog';
import api from '../services/api';
import { getUser, isDemo as checkIsDemo, canViewCameras } from '../services/auth';
import { getListenerPool } from '../utils/heavyInitializations';
import { useTranslation } from 'react-i18next';
import {
  formatAssistantControlNoop,
  planPowerControl,
} from '../services/assistantDeviceControl';
import { eventBus } from '../services/EventBus';

interface Camera {
  id: number;
  name: string;
  enabled: boolean;
}

function readCachedCameraName(cameraId: number): string {
  try {
    const raw = localStorage.getItem('video_cameras_cache');
    if (!raw) {
      return '';
    }
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed?.cameras) ? parsed.cameras : [];
    const found = list.find((camera: { id?: number; name?: string }) => camera.id === cameraId);
    return typeof found?.name === 'string' ? found.name : '';
  } catch {
    return '';
  }
}

function camerasFromIds(ids: number[]): Camera[] {
  return ids.map((id) => ({
    id,
    name: readCachedCameraName(id),
    enabled: true,
  }));
}

function isPinSessionCurrentlyValid(): boolean {
  return typeof window !== 'undefined'
    && typeof (window as any).isPinSessionValid === 'function'
    && Boolean((window as any).isPinSessionValid());
}

interface FloatingCameraWindowProps {
  open: boolean;
  cameraIds: number[];
  displayDuration: number; // en segundos
  deviceName?: string;
  deviceId?: number; // ID del dispositivo
  device?: any; // Dispositivo completo para renderizar la tarjeta debajo
  originalCardWidth?: number; // Ancho de la tarjeta original del dispositivo
  requiresPin?: boolean; // Si el dispositivo requiere PIN
  pendingAction?: string; // Acción pendiente ('toggle' o 'control_cover')
  pendingActionValue?: any; // Valor de la acción pendiente (para control_cover)
  onClose: () => void;
  onPinVerified?: () => void; // Callback cuando se verifica el PIN
  // Props para DeviceCard
  userRole?: string;
  deviceErrors?: Set<number>;
  confirmingDevices?: Set<number>;
  confirmedDevices?: Set<number>;
  coverActions?: Record<number, string>;
  onDeviceClick?: (device: any) => void;
  onDeviceToggle?: (device: any) => void;
  onControlCover?: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
  onDeviceImageClick?: (ip: string) => void;
  deviceTimerStates?: Record<number, { isTimerActive: boolean; timerStartTime?: number }>;
  isDemo?: boolean;
  onShowRestriction?: () => void; // Callback para mostrar snackbar de restricción
  controlAction?: string;
}

function sendPlannedPowerControl(
  device: any,
  deviceId: number,
  pendingAction: string | undefined,
  pendingActionValue: any,
  deviceName: string | undefined,
  controlAction?: string
): boolean {
  if (pendingAction !== 'toggle' && pendingAction !== 'set_power') {
    return false;
  }
  if (!device || !deviceId) {
    return false;
  }
  const ca =
    controlAction ||
    (pendingAction === 'set_power'
      ? pendingActionValue === 'on'
        ? 'encender'
        : 'apagar'
      : 'alternar');
  const plan = planPowerControl(
    device,
    pendingAction || 'toggle',
    pendingActionValue,
    ca
  );
  if (plan.kind === 'noop' && plan.noopReason) {
    const name = device.nombre || deviceName || `Dispositivo ${deviceId}`;
    eventBus.emit('assistant:control_result', {
      device_id: deviceId,
      success: true,
      message: formatAssistantControlNoop(name, plan.noopReason),
      noop: true,
    });
    return true;
  }
  const newState = plan.targetState!;
  const WebSocketService = (window as any).WebSocketService;
  if (WebSocketService) {
    const listenerId = WebSocketService.assignListener(deviceId);
    if (listenerId !== null) {
      WebSocketService.sendDeviceControl(deviceId, 'toggle', newState);
    }
  }
  return true;
}

const FloatingCameraWindow: React.FC<FloatingCameraWindowProps> = ({
  open,
  cameraIds,
  displayDuration,
  deviceName,
  deviceId,
  device,
  originalCardWidth,
  requiresPin = false,
  pendingAction,
  pendingActionValue,
  controlAction,
  onClose,
  onPinVerified,
  userRole,
  deviceErrors = new Set(),
  confirmingDevices = new Set(),
  confirmedDevices = new Set(),
  coverActions = {},
  onDeviceClick,
  onDeviceToggle,
  onControlCover,
  onDeviceImageClick,
  deviceTimerStates = {},
  isDemo = false,
  onShowRestriction
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(displayDuration);
  const [cameras, setCameras] = useState<Camera[]>(() => camerasFromIds(cameraIds));
  const [, setWindowHeight] = useState(0); // Altura de la ventana flotante
  const [, setCardWidth] = useState<number>(originalCardWidth || 600); // Ancho de la tarjeta
  const [showPinDialog, setShowPinDialog] = useState(
    () => Boolean(requiresPin) && !isPinSessionCurrentlyValid()
  );
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const initialDurationRef = useRef(displayDuration);
  const onCloseRef = useRef(onClose);
  const windowRef = useRef<HTMLDivElement>(null);
  const videoExecutedRef = useRef(false); // Para ejecutar el control solo una vez
  
  // Estados para zoom por cámara
  const [videoZoom, setVideoZoom] = useState<Record<number, { scale: number; position: { x: number; y: number } }>>({});
  const [touchStates, setTouchStates] = useState<Record<number, {
    lastTouchDistance: number | null;
    isDragging: boolean;
    dragStart: { x: number; y: number };
  }>>({});

  const { t } = useTranslation();
  const user = getUser();
  const currentUserRole = userRole || (user?.role || 'user');
  const userIsDemo = user ? checkIsDemo(user) : false;
  const hasCameraPermission = canViewCameras(user);

  // MEJORA: Pre-inicializar pool de listeners y verificar WebSocket INMEDIATAMENTE (sin delay)
  // Esto asegura que todo esté listo antes de que el usuario presione cualquier botón en la tarjeta
  useEffect(() => {
    if (open) {
      // MEJORA: Pre-inicializar INMEDIATAMENTE sin delay - el DOM ya está renderizado
      // Pre-inicializar pool de listeners
      getListenerPool();
      
      // Verificar que WebSocket esté conectado
      if (typeof (window as any).WebSocketService !== 'undefined') {
        const wsService = (window as any).WebSocketService;
        if (!wsService.isConnected()) {
          // Intentar reconectar si no está conectado
          const token = localStorage.getItem('token');
          if (token) {
            wsService.connect(token);
          }
        }
        
        // Verificar que el listener de respuestas esté configurado
        wsService.setupControlResponseListener();
      }
    }
  }, [open]); // Cuando se abre la ventana

  // Actualizar refs cuando cambian los props
  useEffect(() => {
    onCloseRef.current = onClose;
    if (open) {
      const durationChanged = initialDurationRef.current !== displayDuration;
      initialDurationRef.current = displayDuration;

      if (durationChanged && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setRemainingSeconds(displayDuration);
        if (displayDuration > 0) {
          timerRef.current = setInterval(() => {
            setRemainingSeconds(prev => {
              const next = prev - 1;
              if (next <= 0) {
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                onCloseRef.current();
                return 0;
              }
              return next;
            });
          }, 1000);
        }
      }

      // CRITICO: Solo resetear videoExecutedRef cuando se abre por primera vez
      // NO resetear si pendingAction cambió (eso se maneja en el otro useEffect)
      const isFirstOpen = prevPendingActionRef.current === undefined;
      if (isFirstOpen) {
        videoExecutedRef.current = false;
      }
      
      // CRITICO: Mostrar PIN dialog INMEDIATAMENTE si requiere PIN y NO hay sesión válida
      // Esto permite que el PIN dialog y la ventana de vídeo se abran en paralelo
      // No esperar a que se carguen las cámaras
      // Los usuarios demo SÍ pueden ver el dialog pero NO pueden ingresar el PIN
      if (requiresPin) {
        const pinValid = (window as any).isPinSessionValid && (window as any).isPinSessionValid();
        setShowPinDialog(!pinValid);
      } else {
        setShowPinDialog(false);
      }
      // Agregar entrada al historial cuando se abre la ventana flotante
      window.history.pushState({ page: 'floating-camera-window' }, '', '');
    } else {
      // Resetear cuando se cierra
      setShowPinDialog(false);
    }
  }, [onClose, open, displayDuration, requiresPin]);

  // Manejar el botón back físico del teléfono
  useEffect(() => {
    if (!open) return;

    const handlePopState = (event: PopStateEvent) => {
      // Cerrar todo directamente (diálogo de PIN y ventana flotante) y volver a la vista anterior
      onClose();
      event.preventDefault();
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, onClose]);

  // Actualizar ancho de tarjeta si se proporciona
  useEffect(() => {
    if (originalCardWidth && originalCardWidth > 0) {
      setCardWidth(originalCardWidth);
    }
  }, [originalCardWidth]);

  // El stream arranca con los IDs que ya tenemos. El GET solo actualiza
  // nombre / enabled; no bloquea el video en vivo.
  useEffect(() => {
    if (!open || cameraIds.length === 0) {
      return;
    }

    setCameras((prev) => {
      const prevById = new Map(prev.map((camera) => [camera.id, camera]));
      const next = cameraIds.map((id) => {
        const existing = prevById.get(id);
        return existing || { id, name: readCachedCameraName(id), enabled: true };
      });
      const unchanged =
        next.length === prev.length &&
        next.every((camera, index) => camera.id === prev[index].id);
      return unchanged ? prev : next;
    });

    let cancelled = false;
    const loadCameraNames = async () => {
      try {
        const response = await api.get('/video/cameras');
        if (cancelled || !response.data.success || !response.data.cameras) {
          return;
        }
        const camerasById = new Map(
          (response.data.cameras as Camera[]).map((camera) => [camera.id, camera])
        );
        const filteredCameras = cameraIds
          .map((id) => camerasById.get(id))
          .filter((camera): camera is Camera => Boolean(camera && camera.enabled));
        if (filteredCameras.length > 0) {
          setCameras(filteredCameras);
        }
      } catch {
        /* seguir con los IDs ya montados */
      }
    };
    void loadCameraNames();
    return () => {
      cancelled = true;
    };
  }, [open, cameraIds]);

  useEffect(() => {
    if (!open) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRemainingSeconds(initialDurationRef.current);
      setCameras([]);
      setShowPinDialog(false);
      videoExecutedRef.current = false;
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (initialDurationRef.current > 0) {
      setRemainingSeconds(initialDurationRef.current);
      timerRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          const next = prev - 1;
          if (next <= 0) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onCloseRef.current();
            return 0;
          }
          return next;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open]);

  // Refs para las funciones de control para evitar re-renderizados infinitos
  const onDeviceToggleRef = useRef(onDeviceToggle);
  const onControlCoverRef = useRef(onControlCover);
  const deviceRef = useRef(device);
  const deviceIdRef = useRef(deviceId);
  const pendingActionRef = useRef(pendingAction);
  const pendingActionValueRef = useRef(pendingActionValue);
  const controlActionRef = useRef(controlAction);
  const prevPendingActionRef = useRef<string | undefined>(pendingAction); // Para detectar cambios en pendingAction
  
  useEffect(() => {
    // CRITICO: Actualizar refs cuando cambian
    onDeviceToggleRef.current = onDeviceToggle;
    onControlCoverRef.current = onControlCover;
    deviceRef.current = device;
    deviceIdRef.current = deviceId;
    pendingActionRef.current = pendingAction;
    pendingActionValueRef.current = pendingActionValue;
    controlActionRef.current = controlAction;
    
    // CRITICO SEGURIDAD: Solo resetear videoExecutedRef cuando pendingAction cambia Y es un click desde dentro
    // NO resetear si es la primera vez que se abre la ventana
    if (open && pendingAction) {
      const pendingActionChanged = prevPendingActionRef.current !== undefined && 
                                   prevPendingActionRef.current !== pendingAction;
      if (pendingActionChanged) {
        // pendingAction cambió mientras la ventana ya estaba abierta = click desde dentro
        // Resetear videoExecutedRef para permitir nueva ejecución
        videoExecutedRef.current = false;
      }
    }
    
    prevPendingActionRef.current = pendingAction;
  }, [onDeviceToggle, onControlCover, device, deviceId, pendingAction, pendingActionValue, controlAction, open]);

  useEffect(() => {
    if (!requiresPin) {
      return;
    }

    if (!isPinSessionCurrentlyValid()) {
      return;
    }

    if (!open || cameraIds.length === 0 || showPinDialog || !pendingActionRef.current || !deviceRef.current) {
      return;
    }

    if (videoExecutedRef.current) {
      return;
    }

    videoExecutedRef.current = true;

    getListenerPool();

    if (typeof (window as any).WebSocketService !== 'undefined') {
      const wsService = (window as any).WebSocketService;
      if (!wsService.isConnected()) {
        const token = localStorage.getItem('token');
        if (token) {
          wsService.connect(token);
        }
      }
      wsService.setupControlResponseListener();
    }

    if (
      sendPlannedPowerControl(
        deviceRef.current,
        deviceIdRef.current!,
        pendingActionRef.current,
        pendingActionValueRef.current,
        deviceName,
        controlActionRef.current
      )
    ) {
      /* enviado o noop */
    } else if (pendingActionRef.current === 'control_cover' && deviceIdRef.current && pendingActionValueRef.current) {
      const coverDeviceId = deviceIdRef.current;
      const action = pendingActionValueRef.current;

      const WebSocketService = (window as any).WebSocketService;
      if (WebSocketService) {
        const listenerId = WebSocketService.assignListener(coverDeviceId);
        if (listenerId !== null) {
          if (action === 'stop') {
            WebSocketService.sendDeviceControl(coverDeviceId, 'stop');
          } else {
            const channel = action === 'up' ? 0 : 1;
            WebSocketService.sendDeviceControl(coverDeviceId, 'toggle', true, channel);
          }
        }
      }
    }
  }, [open, cameraIds.length, showPinDialog, requiresPin, deviceName]);

  // Manejar verificación de PIN
  // CRITICO SEGURIDAD: Esta función se llama DESPUÉS de que el usuario ingresa el PIN correcto
  // CRITICO SEGURIDAD: SOLO aquí se ejecuta el control si requiere PIN - NUNCA antes
  // CRITICO SEGURIDAD: NUNCA ejecutar si ya se ejecutó (videoExecutedRef) para evitar doble envío
  const handlePinVerified = useCallback(() => {
    // CRITICO SEGURIDAD: Verificar videoExecutedRef PRIMERO - si ya se ejecutó, NUNCA ejecutar de nuevo
    // Esto previene doble envío en caso de que el useEffect de arriba ya haya ejecutado el comando
    if (videoExecutedRef.current) {
      // Ya se ejecutó, NUNCA ejecutar de nuevo - esto es crítico para seguridad
      return;
    }
    
    // CRITICO SEGURIDAD: Marcar como ejecutado INMEDIATAMENTE ANTES de cualquier otra operación
    // Esto previene race conditions donde múltiples llamadas podrían pasar la verificación
    videoExecutedRef.current = true;
    
    // CRITICO SEGURIDAD: Cerrar PIN dialog INMEDIATAMENTE para evitar que se vuelva a mostrar
    setShowPinDialog(false);
    
    // CRITICO: Asegurar que los listeners estén listos ANTES de ejecutar el control
    // Pre-inicializar pool de listeners y WebSocket
    getListenerPool();
    
    if (typeof (window as any).WebSocketService !== 'undefined') {
      const wsService = (window as any).WebSocketService;
      if (!wsService.isConnected()) {
        const token = localStorage.getItem('token');
        if (token) {
          wsService.connect(token);
        }
      }
      wsService.setupControlResponseListener();
    }
    
    // CRITICO: Ejecutar el control DIRECTAMENTE sin pasar por handleDeviceToggle
    // porque el PIN ya fue verificado. Si pasamos por handleDeviceToggle, volverá a verificar
    // el PIN y podría intentar abrir la ventana flotante innecesariamente.
    if (
      sendPlannedPowerControl(
        deviceRef.current,
        deviceIdRef.current!,
        pendingActionRef.current,
        pendingActionValueRef.current,
        deviceName,
        controlActionRef.current
      )
    ) {
      /* enviado o noop */
    } else if (pendingActionRef.current === 'control_cover' && deviceIdRef.current && pendingActionValueRef.current) {
      const deviceId = deviceIdRef.current;
      const action = pendingActionValueRef.current;
      
      // Asignar listener ANTES de enviar comando
      const WebSocketService = (window as any).WebSocketService;
      if (WebSocketService) {
        const listenerId = WebSocketService.assignListener(deviceId);
        if (listenerId !== null) {
          if (action === 'stop') {
            WebSocketService.sendDeviceControl(deviceId, 'stop');
          } else {
            const channel = action === 'up' ? 0 : 1;
            WebSocketService.sendDeviceControl(deviceId, 'toggle', true, channel);
          }
        }
      }
    }
    
    if (onPinVerified) {
      onPinVerified();
    }
  }, [onPinVerified, deviceName]);

  // Actualizar altura de la ventana cuando cambia
  useEffect(() => {
    if (windowRef.current) {
      const updateHeight = () => {
        if (windowRef.current) {
          const rect = windowRef.current.getBoundingClientRect();
          setWindowHeight(rect.height);
        }
      };
      
      const resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(windowRef.current);
      updateHeight();
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [open, cameras.length]);

  if (!open || cameraIds.length === 0) {
    return null;
  }

  // Calcular distancia entre dos puntos táctiles
  const getTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handlers de touch para zoom por cámara
  const handleTouchStart = (cameraId: number, e: React.TouchEvent) => {
    e.stopPropagation();
    const currentState = touchStates[cameraId] || { lastTouchDistance: null, isDragging: false, dragStart: { x: 0, y: 0 } };
    const currentZoom = videoZoom[cameraId] || { scale: 1, position: { x: 0, y: 0 } };

    if (e.touches.length === 2) {
      // Dos dedos: gesto de pellizco
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setTouchStates({
        ...touchStates,
        [cameraId]: {
          ...currentState,
          lastTouchDistance: distance,
          isDragging: false,
        }
      });
    } else if (e.touches.length === 1 && currentZoom.scale > 1) {
      // Un dedo: arrastrar cuando hay zoom
      setTouchStates({
        ...touchStates,
        [cameraId]: {
          ...currentState,
          isDragging: true,
          dragStart: {
            x: e.touches[0].clientX - currentZoom.position.x,
            y: e.touches[0].clientY - currentZoom.position.y,
          }
        }
      });
    }
  };

  const handleTouchMove = (cameraId: number, e: React.TouchEvent) => {
    e.stopPropagation();
    const currentState = touchStates[cameraId] || { lastTouchDistance: null, isDragging: false, dragStart: { x: 0, y: 0 } };
    const currentZoom = videoZoom[cameraId] || { scale: 1, position: { x: 0, y: 0 } };

    if (e.touches.length === 2 && currentState.lastTouchDistance !== null) {
      // Gestos de pellizco: zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const scaleChange = distance / currentState.lastTouchDistance;
      const newScale = Math.max(1, Math.min(5, currentZoom.scale * scaleChange));
      setVideoZoom({
        ...videoZoom,
        [cameraId]: {
          ...currentZoom,
          scale: newScale,
        }
      });
      setTouchStates({
        ...touchStates,
        [cameraId]: {
          ...currentState,
          lastTouchDistance: distance,
        }
      });
    } else if (e.touches.length === 1 && currentState.isDragging && currentZoom.scale > 1) {
      // Arrastrar cuando hay zoom
      e.preventDefault();
      const newX = e.touches[0].clientX - currentState.dragStart.x;
      const newY = e.touches[0].clientY - currentState.dragStart.y;
      
      // Calcular límites de movimiento basados en el nivel de zoom
      // Cuanto más zoom, más se puede mover
      const maxMove = (currentZoom.scale - 1) * 100;
      setVideoZoom({
        ...videoZoom,
        [cameraId]: {
          ...currentZoom,
          position: {
            x: Math.max(-maxMove, Math.min(maxMove, newX)),
            y: Math.max(-maxMove, Math.min(maxMove, newY)),
          }
        }
      });
    }
  };

  const handleTouchEnd = (cameraId: number) => {
    setTouchStates({
      ...touchStates,
      [cameraId]: {
        lastTouchDistance: null,
        isDragging: false,
        dragStart: { x: 0, y: 0 },
      }
    });
  };

  // Resetear zoom al doble tap
  const handleDoubleClick = (cameraId: number) => {
    setVideoZoom({
      ...videoZoom,
      [cameraId]: { scale: 1, position: { x: 0, y: 0 } }
    });
  };

  const cameraTitle = cameras
    .map((camera) => camera.name)
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          maxHeight: '100dvh',
          backgroundColor: '#000',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          paddingX: 0,
          paddingTop: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            maxWidth: '100%',
            flex: 1,
            gap: 0,
            boxSizing: 'border-box',
            marginTop: 0,
            position: 'relative',
            pointerEvents: 'auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minHeight: 44,
              paddingTop: 'env(safe-area-inset-top)',
              paddingLeft: 1.5,
              paddingRight: 1,
              backgroundColor: '#000',
              zIndex: 10004,
            }}
          >
            <Typography
              sx={{
                color: showPinDialog ? '#aaa' : '#fff',
                fontSize: '0.85rem',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {showPinDialog ? t('pinInput.requiredMessage') : cameraTitle}
            </Typography>
            {displayDuration > 0 && (
              <Typography
                sx={{
                  color: remainingSeconds <= 5 ? '#ff6b6b' : '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  minWidth: '36px',
                  textAlign: 'center',
                  flex: '0 0 auto',
                  mr: 0.5,
                }}
              >
                {remainingSeconds}s
              </Typography>
            )}
            <IconButton
              onClick={onClose}
              aria-label="Cerrar"
              sx={{
                color: '#fff',
                width: 40,
                height: 40,
                flex: '0 0 auto',
                marginRight: '12px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>

          {cameraIds.length > 0 && (
            <Box
              ref={windowRef}
              sx={{
                width: '100%',
                backgroundColor: '#000',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
                marginTop: 0,
                position: 'relative',
                borderRadius: 0,
              }}
            >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                padding: 0,
                overflow: 'hidden',
                minHeight: 0,
                width: '100%',
              }}
            >
              {cameras.map((camera) => {
                const currentZoom = videoZoom[camera.id] || { scale: 1, position: { x: 0, y: 0 } };
                return (
                  <Box
                    key={camera.id}
                    onTouchStart={(e) => handleTouchStart(camera.id, e)}
                    onTouchMove={(e) => handleTouchMove(camera.id, e)}
                    onTouchEnd={() => handleTouchEnd(camera.id)}
                    onDoubleClick={() => handleDoubleClick(camera.id)}
                    sx={{
                      position: 'relative',
                      backgroundColor: '#000',
                      borderRadius: 0,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: showPinDialog ? 'flex-end' : 'center',
                      justifyContent: 'center',
                      width: '100%',
                      minHeight: 0,
                      flex: '1 1 auto',
                      touchAction: 'none',
                      cursor: currentZoom.scale > 1 ? 'move' : 'default',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        transform: `scale(${currentZoom.scale}) translate(${currentZoom.position.x}px, ${currentZoom.position.y}px)`,
                        transformOrigin: 'center center',
                        transition: currentZoom.scale === 1 && currentZoom.position.x === 0 && currentZoom.position.y === 0 ? 'transform 0.3s ease' : 'none',
                      }}
                    >
                      {!hasCameraPermission ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#1a1a1a',
                            color: '#888',
                            padding: 3,
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#888', fontSize: '0.85rem', lineHeight: 1.5 }}>
                            {t('videoStreaming.noCameraPermission')}
                          </Typography>
                        </Box>
                      ) : userIsDemo && camera.name !== 'Calle' ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#1a1a1a',
                            color: '#888',
                            padding: 3,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#2391FF',
                              marginBottom: 1,
                              fontSize: '1rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {t('videoStreaming.cameraTab.demoRestriction', 'Camera View Restricted')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#888',
                              fontSize: '0.85rem',
                              lineHeight: 1.5,
                              marginBottom: 0.5,
                            }}
                          >
                            {t('videoStreaming.cameraTab.demoOnlyStreet', 'Demo users can only see the street camera.')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#888',
                              fontSize: '0.85rem',
                              lineHeight: 1.5,
                              marginBottom: 0.5,
                            }}
                          >
                            {t('videoStreaming.cameraTab.demoOtherCameraMessage', 'Here you would see other camera stream.')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#888',
                              fontSize: '0.85rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {t('videoStreaming.cameraTab.demoCannotViewInside', 'Demo users cannot view other inside cameras.')}
                          </Typography>
                        </Box>
                      ) : (
                        <VideoStreamViewer
                          cameraId={camera.id}
                          cameraName={camera.name}
                          showBandwidth={false}
                          objectPosition={showPinDialog ? 'center bottom' : 'center'}
                        />
                      )}
                    </Box>
                </Box>
                );
              })}
            </Box>
          </Box>
          )}

          {device && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: '0 0 auto',
                paddingTop: 0,
                paddingBottom: 0,
                backgroundColor: '#000',
                borderRadius: 0,
                position: 'relative',
                zIndex: 10002,
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ width: '100%', position: 'relative' }}>
                <DeviceCard
                  device={device}
                  userRole={currentUserRole}
                  deviceErrors={deviceErrors}
                  confirmingDevices={confirmingDevices}
                  confirmedDevices={confirmedDevices}
                  coverActions={coverActions}
                  onDeviceClick={onDeviceClick}
                  onDeviceToggle={onDeviceToggle}
                  onControlCover={onControlCover}
                  onDeviceImageClick={onDeviceImageClick}
                  deviceTimerStates={deviceTimerStates}
                  isDemo={isDemo}
                  onDemoRestriction={onShowRestriction}
                />
              </Box>
            </Box>
          )}

          {showPinDialog && deviceId && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                flex: '0 0 auto',
                backgroundColor: '#1a1a1a',
                borderTop: 'none',
                paddingTop: 0.5,
                paddingBottom: 1,
                position: 'relative',
                zIndex: 10000,
                borderRadius: 0,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: '100%' }}>
                <PinInputDialog
                  open={showPinDialog}
                  embedded={true}
                  onClose={() => {
                    setShowPinDialog(false);
                  }}
                  onPinVerified={handlePinVerified}
                  deviceId={deviceId}
                  onShowRestriction={onShowRestriction}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default FloatingCameraWindow;
